import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppSelector, useAppDispatch } from '@/store';
import { closeModal, openModal, selectActiveModal, selectModalData } from '@/store/slices/uiSlice';
import type { Task } from '@/types';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

// Import all modals
import CreateTaskModal from '@/features/tasks/components/CreateTaskModal';
import TaskDetailModal from '@/features/tasks/components/TaskDetailModal';
import CreateProjectModal from '@/features/projects/components/CreateProjectModal';
import {
  LeaveRequestModal,
  TimeCorrectionModal,
  MissReasonModal,
  ReviewFeedbackModal,
  ConfirmationModal,
} from './modals';

/**
 * GlobalModals
 * Centralized modal manager that renders modals based on uiSlice state
 * Allows any component to trigger modals via Redux actions
 */
const GlobalModals: React.FC = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const activeModal = useAppSelector(selectActiveModal);
  const modalData = useAppSelector(selectModalData);

  const handleClose = () => {
    dispatch(closeModal());
  };

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      dispatch(closeModal());
    },
  });

  return (
    <>
      {/* Task Create Modal */}
      <CreateTaskModal
        open={activeModal === 'taskCreate'}
        onClose={handleClose}
        projectId={modalData?.projectId as string | undefined}
      />

      {/* Task Edit Modal */}
      <CreateTaskModal
        open={activeModal === 'taskEdit'}
        onClose={handleClose}
        projectId={modalData?.projectId as string | undefined}
        initialData={modalData as Record<string, unknown> | undefined}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        open={activeModal === 'taskDetail'}
        onClose={handleClose}
        taskId={(modalData?.taskId as string) || null}
        onEdit={(task: Task) => {
          dispatch(closeModal());
          setTimeout(() => {
            dispatch(openModal({ type: 'taskEdit', data: task }));
          }, 100);
        }}
        onDelete={(taskId: string) => {
          deleteTaskMutation.mutate(taskId);
        }}
      />

      {/* Project Create Modal */}
      <CreateProjectModal
        open={activeModal === 'projectCreate'}
        onClose={handleClose}
      />

      {/* Project Edit Modal */}
      <CreateProjectModal
        open={activeModal === 'projectEdit'}
        onClose={handleClose}
        initialData={modalData as Record<string, unknown> | undefined}
      />

      {/* Leave Request Modal */}
      <LeaveRequestModal
        open={activeModal === 'leaveRequest'}
        onClose={handleClose}
      />

      {/* Time Correction Modal */}
      <TimeCorrectionModal
        open={activeModal === 'timeCorrection'}
        onClose={handleClose}
      />

      {/* Miss Reason Modal */}
      <MissReasonModal
        open={activeModal === 'missReason'}
        onClose={handleClose}
        data={modalData as { taskId?: string; taskTitle?: string; missedDate?: string }}
      />

      {/* Review Feedback Modal */}
      <ReviewFeedbackModal
        open={activeModal === 'reviewFeedback'}
        onClose={handleClose}
        data={modalData as { 
          reviewId?: string; 
          employeeName?: string; 
          employeeAvatar?: string;
          reviewPeriod?: string;
          reviewType?: 'self' | 'peer' | 'manager';
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={activeModal === 'confirmation'}
        onClose={handleClose}
        data={modalData as {
          title?: string;
          message?: string;
          confirmText?: string;
          cancelText?: string;
          type?: 'warning' | 'danger' | 'info' | 'success';
          onConfirm?: (reason?: string) => void | Promise<void>;
          reasonInput?: {
            label?: string;
            placeholder?: string;
            required?: boolean;
          };
        }}
      />
    </>
  );
};

export default GlobalModals;
